import ButtonVariants from "@core-components/button/ButtonVariants";
import EPublicRoutes from "@core-types/routes.enum";
import useAppLanguage from "@hooks/language/useAppLanguage";
import useAppTranslation from "@hooks/language/useAppTranslation";
import {
  Autocomplete,
  type AutocompleteProps,
  Avatar,
  Badge,
  Flex,
  Loader,
  Text,
} from "@mantine/core";
import { IconSearch, IconX } from "@tabler/icons-react";
import clsx from "clsx";
import React, { useMemo } from "react";
import { Link } from "react-router";
import type IChampion from "../../shared/models/IChampion.model";

interface Props {
  onChange?: (input: string) => void;
  ref?: React.RefObject<HTMLInputElement | null>;
  placeholder?: string;
  ariaLabel?: string;
  className?: string;
  rightSection?: React.ReactNode;
  onSelect?: (value: string) => void;
  isLoading?: boolean;
  withLeftSection?: boolean;
  classNames?: AutocompleteProps["classNames"] & {
    text?: string;
  };
  isNew?: boolean;
  maxSize?: string;
  value?: string;
  values: IChampion[];
}

const SSSearch = React.forwardRef<HTMLInputElement, Props>(
  (
    {
      onChange,
      placeholder,
      ariaLabel,
      className,
      rightSection,
      onSelect,
      isLoading,
      withLeftSection = true,
      classNames,
      isNew = false,
      maxSize = "max-w-[500px]",
      value,
      values,
    },
    ref,
  ) => {
    const { t } = useAppTranslation("components.search");
    const { locationUrlBuilder } = useAppLanguage();
    const getChampionBySkinSlug = (slug: string) =>
      values.find((champion) =>
        champion.skins.find((skin) => skin.slug === slug),
      );

    const getSkinBySlug = (slug: string, champion: IChampion) =>
      champion.skins.find((skin) => skin.slug === slug);

    const renderAutocompleteOption: AutocompleteProps["renderOption"] = ({
      option,
    }) => {
      const champion = getChampionBySkinSlug(option.value);

      if (!champion) {
        return null;
      }

      const skin = getSkinBySlug(option.value, champion);

      if (!skin) {
        return null;
      }

      return (
        <Flex
          className="hover-scale w-full pl-1 no-underline"
          component={Link}
          direction={"row"}
          gap="sm"
          to={locationUrlBuilder(
            EPublicRoutes.SMURFS_SKIN,
            champion.slug,
            skin.slug,
          )}
        >
          <Avatar
            alt={t("skin", { name: skin.name })}
            imageProps={{
              title: t("skin", { name: skin.name }),
            }}
            radius="xl"
            size={36}
            src={skin.thumbnail}
          />
          <div>
            <Text className={getClassNames("text")} size="sm">
              {skin.name}
            </Text>
            <Text className={getClassNames("text")} opacity={0.5} size="xs">
              {t("stock", { count: Math.max(skin.stock, 0) })}
            </Text>
          </div>
        </Flex>
      );
    };

    const getClassNames = (key: string): string | undefined => {
      if (!classNames) {
        return undefined;
      }
      // @ts-expect-error - Cannot access key with a string, but it works :)
      return classNames[key] as string;
    };

    const rightSelectionComponent = useMemo(() => {
      if (isLoading) return <Loader color="grey" />;

      if (rightSection) return rightSection;

      return (
        <ButtonVariants.ActionIcon
          title={t("clear")}
          showDefault={false}
          variant="transparent"
          onClick={() => onChange?.("")}
        >
          <IconX />
        </ButtonVariants.ActionIcon>
      );
    }, [onChange, rightSection, t, isLoading]);

    return (
      <div className={`z-50 w-full ${maxSize} group relative`}>
        <div className="group-focus-within:gradient-search-bar -translate-x-1/2 invisible absolute top-full left-1/2 z-0 h-[500px] w-svw transition-all group-focus-within:visible" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div
            className="size-96 scale-0 rounded-full border-8 border-blue-500 group-focus-within:animate-grow-disappear"
            style={{
              animationDelay: "0.2s",
            }}
          />
        </div>
        <Autocomplete
          ref={ref}
          aria-label={ariaLabel ?? t("ariaLabel")}
          className={className}
          classNames={{
            input: clsx(
              "bg-transparent border-none text-primary-white",
              getClassNames("input"),
            ),
            root: clsx(
              `w-full ${maxSize} z-50 relative rounded-lg py-2 px-4 default-icon border border-transparent border-2 focus-within:border-secondary-700 hover-fade ${getClassNames(
                "root",
              )} group-focus-within:animate-shadow-grow`,
              {
                "!bg-gray-400 !select-none !cursor-progress !pointer-events-none":
                  isLoading,
              },
            ),
            dropdown: `mt-2 bg-primary-light border-none rounded-lg shadow-2xl ${getClassNames(
              "dropdown",
            )}`,
            option: `hover:bg-primary-darker text-primary-white ${getClassNames(
              "option",
            )}`,
            groupLabel: `text-primary-dark-text ${getClassNames("groupLabel")}`,
          }}
          comboboxProps={{
            transitionProps: { transition: "fade", duration: 200 },
          }}
          data={values.map((champion) => ({
            group: champion.name,
            items: champion.skins.map((skin) => ({
              label: skin.name,
              value: skin.slug,
            })),
          }))}
          leftSection={withLeftSection ? <IconSearch size={15} /> : null}
          limit={10}
          placeholder={placeholder ?? t("placeholder")}
          renderOption={renderAutocompleteOption}
          rightSection={rightSelectionComponent}
          value={value}
          onChange={(value) => onChange?.(value)}
          onOptionSubmit={(value) => onSelect?.(value)}
        />
        {isNew && (
          <Badge
            bg="#253D3A"
            c="#00d991"
            className="left- -rotate-[20deg] -translate-x-7 -translate-y-3 absolute top-0 z-50 select-none"
            radius={5}
          >
            {t("new")}
          </Badge>
        )}
      </div>
    );
  },
);

SSSearch.displayName = "Search";

export default SSSearch;
